import { Auth0Provider } from "@auth0/auth0-react";
import { LinearProgress } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LicenseInfo } from "@mui/x-license";
import { ErrorBoundary } from "@sentry/react";
import { Suspense } from "react";
import { Outlet, ScrollRestoration, useNavigate, useSearchParams } from "react-router";
import { config } from "./config";
import { AnalyticsMetadata } from "./modules/analytics/analytics";
import { BrainFish } from "./modules/brainfish/brainfish";
import { AccountLimits } from "./modules/context/account-limits";
import { DesignHuddleLogin } from "./modules/design-huddle/design-huddle-login";
import { AppError } from "./modules/generic/components/errors/app-error";
import { Token } from "./server/auth/token";
import { MomentCardDragAndDropProvider } from "./utils/contexts/moment-card-drag-and-drop-context";
import { SettingsProvider } from "./utils/contexts/settings-context";
import { getOrganization } from "./utils/organization";

import type { FC } from "react";

export const App: FC = () => {
  const [searchparams] = useSearchParams();
  const navigate = useNavigate();
  LicenseInfo.setLicenseKey(config.mui_x_pro.licence);

  return (
    <ErrorBoundary fallback={({ resetError }) => <AppError onRetry={resetError} />}>
      <Auth0Provider
        domain={config.auth0.issuer_base_domain}
        clientId={config.auth0.client_id}
        authorizationParams={{
          redirect_uri: location.origin,
          audience: config.auth0.audience,
          scope: "openid profile email supabase_token",
          invitation: searchparams.get("invitation") || undefined,
          organization: getOrganization(searchparams),
        }}
        useRefreshTokens={true}
        useRefreshTokensFallback={true}
        cacheLocation="localstorage"
        skipRedirectCallback={window.location.pathname !== "/"}
        onRedirectCallback={(appState) => {
          void navigate(appState?.returnTo || window.location.pathname);
        }}
      >
        <AccountLimits>
          <AnalyticsMetadata />
          <DesignHuddleLogin />
          <BrainFish />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MomentCardDragAndDropProvider>
              <SettingsProvider>
                {window.location.pathname !== "/" && (
                  <ErrorBoundary>
                    <Token />
                  </ErrorBoundary>
                )}
                <Suspense fallback={<LinearProgress />}>
                  <ScrollRestoration />
                  <Outlet />
                </Suspense>
              </SettingsProvider>
            </MomentCardDragAndDropProvider>
          </LocalizationProvider>
        </AccountLimits>
      </Auth0Provider>
    </ErrorBoundary>
  );
};
